// Client for the page-annotation API (api.gazar.dev /api/drawings). Everyone
// reads a page's drawing; only the owner (admin) writes. Each stroke is anchored
// to the DOM element it was drawn over so it stays on that content as the page
// reflows — see DrawingLayer.
import { apiBase } from "./content";

export interface DrawingStroke {
  tool: "pen" | "eraser";
  color: string;
  size: number;
  // CSS path to the element the stroke was drawn on ("" = anchored to the page
  // itself, i.e. absolute document coordinates).
  anchor: string;
  // The anchor element's document-space top-left at draw time — fallback base
  // when the anchor can't be resolved on a given client.
  ax: number;
  ay: number;
  // The viewport's horizontal centre at draw time. When the anchor fails to
  // resolve (responsive DOM differs from draw time), the fallback base is shifted
  // by (currentCentre - cx) so the stroke tracks the centred content column
  // instead of pinning to the left edge. Optional: legacy strokes predate it.
  cx?: number;
  // [dx, dy] pixel offsets from the anchor's top-left.
  points: [number, number][];
}

export interface DrawingData {
  strokes: DrawingStroke[];
}

export interface Drawing {
  path: string;
  data: DrawingData;
  updatedAt: string;
}

// Normalise a location into the storage key: pathname only, no query or hash, no
// trailing slash (so "/x" and "/x/" and "/x?y" are one page). Must stay in sync
// with the backend's pathSchema.
export function drawingKey(pathname: string): string {
  let p = pathname.split(/[?#]/)[0] || "/";
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p.startsWith("/") ? p : `/${p}`;
}

export async function fetchDrawing(path: string): Promise<Drawing | null> {
  try {
    const res = await fetch(`${apiBase()}/api/drawings?path=${encodeURIComponent(path)}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { drawing: Drawing | null };
    return data.drawing ?? null;
  } catch {
    return null;
  }
}

export async function saveDrawing(path: string, data: DrawingData): Promise<void> {
  const res = await fetch(`${apiBase()}/api/drawings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ path, data }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message || "Could not save the drawing");
  }
}

export async function clearDrawing(path: string): Promise<void> {
  const res = await fetch(`${apiBase()}/api/drawings?path=${encodeURIComponent(path)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message || "Could not clear the drawing");
  }
}
